/* eslint-disable no-useless-computed-key */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { Popover } from 'react-tiny-popover';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { randomIntFromInterval, TWITTER_LINK } from '../../utils';
import { Menu, MenuItem } from '../design-system';

type DigitOption =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'middle'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom';

type DigitNums = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

const DIGITS: Record<DigitNums, DigitOption[]> = {
  [0]: [
    'top',
    'top-left',
    'top-right',
    'bottom-right',
    'bottom-left',
    'bottom-right',
    'bottom'
  ],
  [1]: ['top-right', 'bottom-right'],
  [2]: ['top', 'top-right', 'middle', 'bottom-left', 'bottom'],
  [3]: ['top', 'top-right', 'middle', 'bottom-right', 'bottom'],
  [4]: ['top-left', 'top-right', 'middle', 'bottom-right', 'bottom-right'],
  [5]: ['top', 'top-left', 'middle', 'bottom-right', 'bottom'],
  [6]: ['top-left', 'middle', 'bottom-left', 'bottom-right', 'bottom'],
  [7]: ['top', 'top-right', 'bottom-right'],
  [8]: [
    'top',
    'top-left',
    'top-right',
    'middle',
    'bottom-right',
    'bottom-left',
    'bottom-right',
    'bottom'
  ],
  [9]: ['top', 'top-left', 'top-right', 'middle', 'bottom-right']
};

const Wrapper = styled.div`
  padding: 5px 0;
  cursor: pointer;
`;

const Inner = styled.div`
  position: relative;
  display: flex;
  height: 32px;
  gap: 5px;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  padding: 5px;
  flex: none;
  transition: background-color ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.15)};
  }

  [data-theme='pink'] & {
    background-color: ${({ theme }) => theme.paletteCssVars.fgA1200};
    border-color: ${({ theme }) => theme.paletteCssVars.fg1200};

    &:hover {
      background-color: ${({ theme }) => theme.paletteCssVars.fgA1100};
    }
  }
`;

const VisitorText = styled.div`
  padding: 5px 15px 10px;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  opacity: 0.7;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
`;

export const HitCounter: React.ComponentType = React.memo(() => {
  const [numToShow, setNumToShow] = useState(1000000);
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);

  const _togglePopover = useCallback(() => {
    setPopoverOpen(prev => !prev);
  }, []);

  useEffect(() => {
    setNumToShow(randomIntFromInterval(1000000, 9999999));
  }, []);

  const arrayOfDigits = useMemo(
    () => Array.from(numToShow.toString()).map(Number),
    [numToShow]
  );

  const visitorText = `You are visitor: ${numToShow.toLocaleString('en-US')}`;

  return (
    <Popover
      align="start"
      containerStyle={{ zIndex: '99999' }}
      content={
        <Menu>
          <VisitorText>{visitorText}</VisitorText>
          <MenuItem
            text="Sign our guest book..."
            onClick={() => window.open(TWITTER_LINK, '_blank')?.focus()}
          />
        </Menu>
      }
      isOpen={isPopoverOpen}
      positions={['bottom', 'top']}
      reposition={true}
      onClickOutside={_togglePopover}>
      <Wrapper title={visitorText} onClick={_togglePopover}>
        <Inner>
          {arrayOfDigits.map((item, index) => (
            <Digit key={index} number={DIGITS[item as DigitNums]} />
          ))}
        </Inner>
      </Wrapper>
    </Popover>
  );
});

const DigitWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 12px;
  gap: 1px;
`;

const Col = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  flex: none;

  ${({ $wide }) =>
    $wide &&
    css`
      flex: 1 1 auto;
    `}
`;

const Line = styled.div<{ $active: boolean; $dir: string }>`
  background: ${({ theme, $active }) =>
    $active ? theme.highlightColor : rgba(theme.highlightColor, 0.28)};

  ${({ $dir }) =>
    $dir === 'horiz' &&
    css`
      width: 100%;
      height: 1px;
    `}

  ${({ $dir }) =>
    $dir === 'vert' &&
    css`
      height: 100%;
      width: 1px;
    `}
`;

interface DigitProps {
  number: DigitOption[];
}

const Digit: React.ComponentType<DigitProps> = React.memo(({ number }) => {
  return (
    <DigitWrapper>
      <Col>
        <Line $active={number.includes('top-left')} $dir="vert" />
        <Line $active={number.includes('bottom-left')} $dir="vert" />
      </Col>
      <Col $wide={true}>
        <Line $active={number.includes('top')} $dir="horiz" />
        <Line $active={number.includes('middle')} $dir="horiz" />
        <Line $active={number.includes('bottom')} $dir="horiz" />
      </Col>
      <Col>
        <Line $active={number.includes('top-right')} $dir="vert" />
        <Line $active={number.includes('bottom-right')} $dir="vert" />
      </Col>
    </DigitWrapper>
  );
});
